import { useState, useEffect } from 'react';
import { X, Loader2, Package } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import type { LabSupplier } from './types';
import { SUPPLIER_TYPE_LABELS } from './constants';

interface SupplierModalProps {
  supplier?: LabSupplier | null;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

export function SupplierModal({ supplier, onSave, onClose }: SupplierModalProps) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: supplier?.name || '',
    url: supplier?.url || '',
    type: supplier?.type || 'WHOLESALER',
    unitPrice: supplier?.unitPrice || '',
    moq: supplier?.moq || '',
    deliveryTime: supplier?.deliveryTime || '',
    notes: supplier?.notes || '',
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        url: form.url.trim() || undefined,
        type: form.type,
        unitPrice: form.unitPrice.trim() || undefined,
        moq: form.moq.trim() || undefined,
        deliveryTime: form.deliveryTime.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-lg p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Package className="w-4 h-4 text-accent-primary" />
            {supplier ? 'Edytuj dostawcę' : 'Dodaj dostawcę'}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-text-muted mb-1 block">Nazwa *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Nazwa dostawcy"
              className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-text-muted mb-1 block">URL</label>
              <input
                type="text"
                value={form.url}
                onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Typ</label>
              <Select
                value={form.type}
                onChange={e => setForm(p => ({ ...p, type: e.target.value as 'MARKETPLACE' | 'WHOLESALER' | 'PRODUCER' | 'DROPSHIPPING' }))}
                className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              >
                {Object.entries(SUPPLIER_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-text-muted mb-1 block">Cena jednostkowa</label>
              <input
                type="text"
                value={form.unitPrice}
                onChange={e => setForm(p => ({ ...p, unitPrice: e.target.value }))}
                placeholder="np. 45 zł"
                className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">MOQ</label>
              <input
                type="text"
                value={form.moq}
                onChange={e => setForm(p => ({ ...p, moq: e.target.value }))}
                placeholder="np. 100 szt."
                className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Czas dostawy</label>
              <input
                type="text"
                value={form.deliveryTime}
                onChange={e => setForm(p => ({ ...p, deliveryTime: e.target.value }))}
                placeholder="np. 3-5 dni"
                className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-text-muted mb-1 block">Notatki</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Dodatkowe informacje..."
              rows={3}
              className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={handleSubmit}
            disabled={!form.name.trim() || saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-accent-primary/20 text-accent-primary rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-accent-primary/30 transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
            {supplier ? 'Zapisz zmiany' : 'Dodaj dostawcę'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-secondary text-sm hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
